import {
  DataSourceSettingsDto,
  MutateWidgetPayload,
  VariableDto,
  WidgetDto,
} from '@thingslog/repositories';
import { DeleteOutline } from '@mui/icons-material';
import { Button } from '@mui/material';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetSettingsForm } from '../../forms';
import { DashboardConfirmationDialog } from '../../confirmation-dialog/DashboardConfirmationDialog';
import { DashboardSidebarWidgetCard } from './DashboardSidebarWidgetCard';

interface Props {
  widgetId: string;
  dashboardWidget: WidgetDto | null;
  dataSources: DataSourceSettingsDto[];
  variables: VariableDto[];
  isPersisting: boolean;
  isDeleting: boolean;
  onPersistWidget: (
    widgetId: string,
    payload: MutateWidgetPayload,
    callbacks?: { onSuccess?: () => void }
  ) => void;
  onDeleteWidget: (widget: WidgetDto, callbacks?: { onSuccess?: () => void }) => void;
  onClose: (widgetId: string) => void;
  onStartTour: () => void;
}

export const DashboardSidebarDashboardWidgetEditWidget: FC<Props> = ({
  widgetId,
  dashboardWidget,
  dataSources,
  variables,
  isPersisting,
  isDeleting,
  onPersistWidget,
  onDeleteWidget,
  onClose,
  onStartTour,
}: Props) => {
  const { t } = useTranslation();
  const [widgetPendingDelete, setWidgetPendingDelete] = useState<WidgetDto | null>(null);

  const handleDelete = (): void => {
    if (dashboardWidget) {
      setWidgetPendingDelete(dashboardWidget);
    }
  };

  const handleDeleteCancel = (): void => {
    setWidgetPendingDelete(null);
  };

  const handleDeleteConfirm = (): void => {
    if (!widgetPendingDelete) {
      return;
    }

    onDeleteWidget(widgetPendingDelete, {
      onSuccess: () => {
        setWidgetPendingDelete(null);
        onClose(widgetId);
      },
    });
  };

  if (!dashboardWidget) {
    return (
      <DashboardSidebarWidgetCard
        title={t('dashboard_edit_widget', 'Edit Widget')}
        widgetId={widgetId}
        onClose={onClose}
        onStartTour={onStartTour}
      >
        <div className="p-4 text-sm text-gray-600">
          {t('dashboard_widget_no_longer_exists', 'Widget no longer exists.')}
        </div>
      </DashboardSidebarWidgetCard>
    );
  }

  return (
    <DashboardSidebarWidgetCard
      title={t('dashboard_edit_named_widget', 'Edit {{name}}', {
        name: dashboardWidget.settings.title || t('dashboard_widget', 'Widget'),
      })}
      widgetId={widgetId}
      expandable
      onClose={onClose}
      onStartTour={onStartTour}
    >
      <WidgetSettingsForm
        type="edit"
        dataSources={dataSources}
        variables={variables}
        defaultValues={{ settings: dashboardWidget.settings }}
        disabled={isPersisting}
        onSubmit={(payload: MutateWidgetPayload): void => {
          onPersistWidget(dashboardWidget.id, payload, { onSuccess: () => onClose(widgetId) });
        }}
        onCancel={(): void => {
          onClose(widgetId);
        }}
      />
      <div className="border-0 border-t border-solid border-gray-200 p-4">
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteOutline />}
          fullWidth
          disabled={isDeleting}
          data-dashboard-tour="widget-form-delete"
          onClick={handleDelete}
        >
          {t('dashboard_delete_widget', 'Delete Widget')}
        </Button>
      </div>
      <DashboardConfirmationDialog
        open={!!widgetPendingDelete}
        title={t('dashboard_delete_widget', 'Delete Widget')}
        confirmLabel={t('dashboard_yes_delete', 'Yes, delete')}
        disabled={isDeleting}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      >
        {t(
          'dashboard_widget_delete_confirmation_prefix',
          'This will permanently delete the widget'
        )}{' '}
        <strong>{widgetPendingDelete?.settings.title || t('dashboard_widget', 'Widget')}</strong>{' '}
        {t(
          'dashboard_widget_delete_confirmation_suffix',
          'from the remote dashboard configuration. This action cannot be undone.'
        )}
      </DashboardConfirmationDialog>
    </DashboardSidebarWidgetCard>
  );
};
